const LEADER_LOCK_NAME = "styx-ws-leader";

type LeadershipHandler = (isLeader: boolean) => void;

function supportsWebLocks(): boolean {
  return typeof navigator !== "undefined" && "locks" in navigator && typeof navigator.locks?.request === "function";
}

/**
 * Keep a single WebSocket leader tab per browser profile.
 * Falls back to "always leader" when Web Locks are unavailable.
 */
export function startTabLeader(handler: LeadershipHandler): () => void {
  if (!supportsWebLocks()) {
    handler(true);
    return () => handler(false);
  }

  let disposed = false;
  let isLeader = false;
  const abort = new AbortController();

  const setLeader = (next: boolean) => {
    if (isLeader === next) return;
    isLeader = next;
    handler(next);
  };

  void (async () => {
    while (!disposed) {
      try {
        await navigator.locks.request(
          LEADER_LOCK_NAME,
          { mode: "exclusive", signal: abort.signal },
          async (lock) => {
            if (!lock || disposed) return;
            setLeader(true);
            await new Promise<void>((resolve) => {
              const done = () => resolve();
              abort.signal.addEventListener("abort", done, { once: true });
            });
          },
        );
      } catch {
        if (disposed) break;
      }
      setLeader(false);
      if (!disposed) {
        await new Promise((resolve) => window.setTimeout(resolve, 250));
      }
    }
  })();

  return () => {
    disposed = true;
    abort.abort();
    setLeader(false);
  };
}
